import React, { useEffect, useRef } from 'react';
import i18n from 'i18n-js';
import moment from 'moment';
import { Formik, Form } from 'formik';
import { FETCH_STATE } from 'constants';
import { BaseModal } from 'components/UI';
import { Box, Grid } from '@material-ui/core';
import { diffObjects, removeEmpty, parseInputNumber } from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { promoCodesActions } from 'state/modules/promoCodes';
import { BasicInformationInputs, initialValues, formSchema } from 'components/PromoCodes';

const UpdatePromoCodeModal = ({ id, open, handleCancel }) => {
  const formRef = useRef(null);
  const dispatch = useDispatch();
  const promoCode = useSelector((state) => state.promoCodes.promoCode);
  const fetchingPromoCode = useSelector((state) => state.promoCodes.fetchingPromoCode);
  const updatePromoCode = useSelector((state) => state.promoCodes.updatePromoCode);

  const loading =
    fetchingPromoCode === FETCH_STATE.LOADING || updatePromoCode === FETCH_STATE.LOADING;

  // Load if id is provided and current PromoCode on app state is different than prop id.
  useEffect(() => {
    if (id && promoCode?.id != id && !loading) {
      dispatch(promoCodesActions.fetchPromoCodeById(id));
    }
  }, [id, promoCode]);

  // Close modal on update success.
  useEffect(() => {
    if (updatePromoCode === FETCH_STATE.DONE) {
      dispatch(promoCodesActions.updatePromoCodeReset());
      handleCancel();
    }
  }, [updatePromoCode]);

  const onSubmit = (params) => {
    let payload = removeEmpty({
      ...params,
      amount: parseInputNumber(params.amount),
    });
    payload = diffObjects(promoCode, payload);

    if (payload.start_time) {
      payload.start_time = moment(payload.start_time)
        .set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        })
        .format();
    }

    if (payload.expiration_time) {
      payload.expiration_time = moment(payload.expiration_time)
        .set({
          hour: 0,
          minute: 0,
          second: 0,
          millisecond: 0,
        })
        .format();
    }
    dispatch(promoCodesActions.updatePromoCode(id, payload));
  };

  const handleSubmit = () => {
    formRef.current.submitForm();
  };

  return (
    <BaseModal
      size="md"
      open={open}
      hasEnterButton
      hasCancelButton
      onEnter={handleSubmit}
      onCancel={handleCancel}
      closeButtonType="icon"
      enableCloseButton={false}
      loading={loading}
      title={i18n.t('promo_codes.update')}
      enterButtonLabel={i18n.t('common.update')}
    >
      <Grid container direction="column" alignItems="flex-start" data-cy="update-promo-code">
        <Box width="100%">
          <Formik
            innerRef={formRef}
            onSubmit={onSubmit}
            initialValues={initialValues(promoCode)}
            validationSchema={formSchema()}
            enableReinitialize
          >
            {({ handleSubmit, errors, values }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <BasicInformationInputs
                  errors={errors}
                  values={values}
                  disabledFields={promoCode.total_emitted > 0 || false}
                />
              </Form>
            )}
          </Formik>
        </Box>
      </Grid>
    </BaseModal>
  );
};

export default UpdatePromoCodeModal;
